import React from "react";
import type {
  Equipment,
  EquipmentAttributeDefinition,
  EquipmentModel,
} from "../../../api/equipment/Equipment.typed";
import type { ShowAlertFn } from "../../../contexts/app/notificationToast";

interface UseEquipmentEditingArgs {
  canManage: boolean;
  selectedModel: EquipmentModel | undefined;
  setEquipments: React.Dispatch<React.SetStateAction<Equipment[]>>;
  showAlert: ShowAlertFn;
  createEquipment: (eq: Equipment) => Promise<unknown>;
  updateEquipment: (eq: Equipment) => Promise<unknown>;
  deleteEquipment: (id: string) => Promise<unknown>;
  reloadCurrentPage: () => void | Promise<void>;
  onAfterDelete?: (id: string) => void;
}

export function useEquipmentEditing({
  canManage,
  selectedModel,
  setEquipments,
  showAlert,
  createEquipment,
  updateEquipment,
  deleteEquipment,
  reloadCurrentPage,
  onAfterDelete,
}: UseEquipmentEditingArgs) {
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editItem, setEditItem] = React.useState<Equipment | null>(null);
  const [equipmentSavePending, setEquipmentSavePending] = React.useState(false);
  const equipmentSaveLockRef = React.useRef(false);

  const openCreate = React.useCallback(() => {
    if (!canManage) {
      showAlert("Vous n'avez pas les droits pour créer un équipement", "danger");

      return;
    }
    if (!selectedModel) return;
    if (editingIndex !== null) {
      showAlert("Veuillez sauvegarder ou annuler l'équipement en cours d'édition", "warning");

      return;
    }
    const blank: Equipment = {
      ModelID: selectedModel.ID!,
      Attributes: {},
    } as Equipment;
    selectedModel.Attributes.forEach((a: EquipmentAttributeDefinition) => {
      blank.Attributes[a.ID] = "";
    });
    setEquipments((prev) => {
      const list = [blank, ...prev];
      setEditingIndex(0);

      return list;
    });
    setEditItem(blank);
  }, [canManage, selectedModel, editingIndex, showAlert, setEquipments]);

  const openEditEquip = React.useCallback(
    (index: number, e: Equipment) => {
      if (editingIndex !== null && editingIndex !== index) {
        showAlert("Veuillez sauvegarder ou annuler l'équipement en cours d'édition", "warning");

        return;
      }
      setEditingIndex(index);
      setEditItem({ ...e });
    },
    [editingIndex, showAlert],
  );

  const saveEquip = React.useCallback(
    async (equipmentOverride?: Equipment, keepEditing?: boolean) => {
      const toSave = equipmentOverride || editItem;
      if (!toSave) return;
      if (!canManage) {
        showAlert("Vous n'avez pas les droits pour enregistrer cet équipement", "danger");

        return;
      }
      if (equipmentSaveLockRef.current) return;
      equipmentSaveLockRef.current = true;
      setEquipmentSavePending(true);
      try {
        if (toSave.ID) await updateEquipment(toSave);
        else await createEquipment(toSave);
        if (!keepEditing) {
          setEditingIndex(null);
          setEditItem(null);
          reloadCurrentPage();
        }
      } finally {
        equipmentSaveLockRef.current = false;
        setEquipmentSavePending(false);
      }
    },
    [canManage, editItem, showAlert, updateEquipment, createEquipment, reloadCurrentPage],
  );

  const cancelEdit = React.useCallback(() => {
    if (editItem && !editItem.ID && editingIndex !== null) {
      setEquipments((prev) => prev.filter((_, idx) => idx !== editingIndex));
    }
    setEquipments((prev) => prev.filter((eq) => eq.ID !== undefined));
    setEditingIndex(null);
    setEditItem(null);
  }, [editItem, editingIndex, setEquipments]);

  const handleDelete = React.useCallback(
    async (id: string) => {
      await deleteEquipment(id);
      reloadCurrentPage();
      onAfterDelete?.(id);
    },
    [deleteEquipment, reloadCurrentPage, onAfterDelete],
  );

  return {
    editingIndex,
    editItem,
    setEditItem,
    equipmentSavePending,
    openCreate,
    openEditEquip,
    saveEquip,
    cancelEdit,
    handleDelete,
  };
}
